// lib/sentry/pii-scrubber.ts
//
// C-19 (audit 2026-05-27): Sentry was initialized with no `beforeSend` or
// `beforeBreadcrumb` hook, so request bodies, headers, and `extra` objects
// shipped to Sentry verbatim. That included E.164 phone numbers, customer
// names, emails, partial card data from North webhooks, and POS / cron
// secrets that landed in error frames. Compliance exposure (GDPR/CCPA/TCPA
// + PCI-adjacent) without operational benefit.
//
// This helper walks event payloads and redacts any key whose lowercase name
// matches our PII set. It is intentionally allow-list-free: scrubbing a few
// false-positive non-PII keys is cheaper than missing a real one.
//
// Used from sentry.client.config.ts, sentry.server.config.ts, and
// sentry.edge.config.ts via Sentry.init's beforeSend + beforeBreadcrumb.

import type { ErrorEvent, EventHint, Breadcrumb } from "@sentry/nextjs"
import { redactSecretPaths, redactSecretText } from "@/lib/privacy/secretPaths"

// Exact-match keys (lowercased) that always get redacted.
const PII_KEYS = new Set<string>([
  // Customer PII
  "customer_phone", "customerphone", "phone", "phone_number", "phonenumber",
  "customer_email", "customeremail", "email",
  "customer_name", "customername", "name", "first_name", "firstname", "last_name", "lastname",
  "address", "street", "city", "zip", "zipcode", "postal_code", "postalcode",
  // Secrets / auth
  "pin", "pin_hash", "password", "password_hash",
  "authorization", "x-pos-auth", "x-cron-secret", "x-north-signature", "x-webhook-signature",
  // Payment data
  "card", "card_number", "cardnumber", "pan", "cvv", "cvv2", "cvc",
  "ssn", "tax_id", "ein", "routing_number", "routingnumber", "account_number", "accountnumber",
  // ABA routing (onboarding_data.step1.abaRouting / getBoardingBankData's
  // return shape) — same tier as routing_number/routingnumber above, just a
  // different field name for the same digits. shouldRedactKey() only
  // lowercases before comparing (no underscore-stripping), so this list can't
  // rely on "abarouting" also catching a hypothetical "aba_routing": there is
  // no snake_case variant of this field anywhere in the codebase (verified),
  // so only the one that actually occurs is added here.
  "abarouting",
  // North credentials
  "north_developer_key", "north_developer_key_enc", "north_password", "north_password_enc",
  // Encryption keys (just in case)
  "encryption_key", "north_encryption_key",
])

// Substring matches (lowercased) — catches things like `customer_phone_e164`,
// `merchant_secret`, `gateway_token`, etc.
const PII_SUBSTRINGS = ["phone", "secret", "token", "passwd", "apikey", "api_key"]

const REDACTED = "[REDACTED]"
const MAX_DEPTH = 6

function shouldRedactKey(key: string): boolean {
  const lower = key.toLowerCase()
  if (PII_KEYS.has(lower)) return true
  return PII_SUBSTRINGS.some((s) => lower.includes(s))
}

function scrub<T>(value: T, depth = 0): T {
  if (depth > MAX_DEPTH || value === null || value === undefined) return value
  if (typeof value !== "object") return value

  if (Array.isArray(value)) {
    return value.map((v) => scrub(v, depth + 1)) as unknown as T
  }

  const out: Record<string, unknown> = {}
  for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
    if (shouldRedactKey(key)) {
      out[key] = REDACTED
    } else {
      out[key] = scrub(v, depth + 1)
    }
  }
  return out as unknown as T
}

/**
 * S-A9 (auditoría 2026-09-07 §6.1): la query string se iba ENTERA a Sentry.
 *
 * `event.request.url` no se tocaba nunca, y `query_string` solo se limpiaba
 * cuando llegaba como objeto — Sentry lo manda como string en la mayoría de los
 * runtimes. El caso concreto: el webhook de pagos de North lleva su secreto
 * compartido en la query (`…/north-b8a9f3c1-4d22?s=<NORTH_WEBHOOK_SECRET>`,
 * porque North no puede firmar cabeceras), así que CUALQUIER excepción en esa
 * ruta publicaba el secreto en Sentry.
 *
 * Se redactan TODOS los valores y se conservan las CLAVES. Deny-by-default a
 * propósito, y por dos razones:
 *   · una allowlist por nombre no habría salvado este caso — el parámetro se
 *     llama `s`, que no casa con ningún patrón de "secreto";
 *   · es la filosofía declarada de este archivo: sobre-redactar sale más barato
 *     que perder uno de verdad.
 * Las claves sobreviven porque son lo que da valor de depuración (qué forma
 * tenía la petición), y no llevan el dato.
 */
function redactQueryValues(qs: string): string {
  if (!qs) return qs
  const params = new URLSearchParams(qs)
  const out = new URLSearchParams()
  let any = false
  for (const key of params.keys()) {
    if (!any) any = true
    out.append(key, REDACTED)
  }
  return any ? out.toString() : qs
}

/** Igual, sobre una URL completa. Devuelve la URL sin tocar si no hay query. */
function redactUrlQuery(url: string): string {
  const q = url.indexOf("?")
  if (q === -1) return url
  // El fragmento (#…) no viaja al servidor, pero si alguien lo mete en un
  // breadcrumb del cliente iría pegado a la query. Se corta con ella.
  const hash = url.indexOf("#", q)
  const rawQs = hash === -1 ? url.slice(q + 1) : url.slice(q + 1, hash)
  const redacted = redactQueryValues(rawQs)
  return redacted ? `${url.slice(0, q)}?${redacted}` : url.slice(0, q)
}

/**
 * El token del link de agenda del socio (/partners/agendar/<token>) va en el
 * PATH, no en la query: `redactUrlQuery` no lo toca. Llega en la URL, en las
 * cabeceras Referer y next-router-state-tree, y suelto en el cuerpo de la server
 * action. Ver lib/privacy/secretPaths.ts.
 */
function redactSecretHeaders(headers: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(headers)) out[k] = typeof v === "string" ? redactSecretText(v) : v
  return out
}

export function scrubPiiBeforeSend(event: ErrorEvent, _hint: EventHint): ErrorEvent {
  if (event.request) {
    if (typeof event.request.data === "string") {
      // El SDK guarda el cuerpo entrante como STRING: `scrub` no toca strings y
      // `token` (p. ej. el de las sustituciones) viajaba a Sentry. Si es JSON de
      // objeto/array se redacta y se re-serializa; si no parsea, queda intacto.
      try {
        const parsed: unknown = JSON.parse(event.request.data)
        if (parsed !== null && typeof parsed === "object") event.request.data = JSON.stringify(scrub(parsed))
      } catch {
        // no es JSON: sin cambios
      }
    } else if (event.request.data) {
      event.request.data = scrub(event.request.data)
    }
    // El token del socio viaja suelto en el cuerpo de la server action (primer argumento).
    if (typeof event.request.data === "string") event.request.data = redactSecretText(event.request.data)
    if (event.request.headers) {
      event.request.headers = redactSecretHeaders(scrub(event.request.headers) as Record<string, string>)
    }
    // S-A9: la URL llevaba la query entera, secreto incluido. Y el path, el token del socio.
    if (typeof event.request.url === "string") {
      event.request.url = redactSecretPaths(redactUrlQuery(event.request.url))
    }
    if (event.request.query_string) {
      // Sentry manda esto como STRING en la mayoría de runtimes; la rama de
      // objeto era la única cubierta, así que el caso normal pasaba de largo.
      event.request.query_string =
        typeof event.request.query_string === "string"
          ? redactQueryValues(event.request.query_string)
          : scrub(event.request.query_string)
    }
  }
  // Sin ruta parametrizada, el nombre de la transacción es el path crudo.
  if (typeof event.transaction === "string") event.transaction = redactSecretPaths(event.transaction)
  if (event.extra) event.extra = scrub(event.extra) as Record<string, unknown>
  if (event.contexts) event.contexts = scrub(event.contexts) as typeof event.contexts
  if (event.tags) event.tags = scrub(event.tags) as typeof event.tags
  if (event.user) {
    // Keep user.id (a UUID, not PII) but scrub anything else that may have crept in.
    event.user = scrub(event.user) as typeof event.user
  }
  return event
}

export function scrubPiiBreadcrumb(crumb: Breadcrumb): Breadcrumb {
  if (crumb.data) {
    crumb.data = scrub(crumb.data) as Record<string, unknown>
    // Los breadcrumbs de fetch/xhr guardan la URL en `data.url`, y `url` no es
    // una clave que `shouldRedactKey` marque — se iba con su query intacta.
    // Va DESPUÉS del scrub: si éste ya la redactó por otro motivo, no es string
    // y no se toca.
    if (typeof crumb.data.url === "string") {
      crumb.data.url = redactSecretPaths(redactUrlQuery(crumb.data.url))
    }
    // Breadcrumbs de navegación: `from` / `to` con el path del link de agenda del socio.
    for (const key of ["from", "to"] as const) {
      const v = crumb.data[key]
      if (typeof v === "string") crumb.data[key] = redactSecretPaths(v)
    }
  }
  if (typeof crumb.message === "string") crumb.message = redactSecretText(crumb.message)
  return crumb
}
